/**
 * 工艺路线模板 API 工厂函数
 * 
 * 提供模板的 CRUD 操作和基于模板生成工艺路线的 API
 */

import type { IRequestClient } from '../core/client';
import { API_ROUTES } from '../routes';
import type { PaginationResult } from '../../types/api';
import type {
  ProcessRouteTemplate,
  ProcessRouteTemplateOperation,
  ProcessRouteTemplateQueryParams,
  CreateProcessRouteTemplateForm,
  UpdateProcessRouteTemplateForm,
  TemplateOperationForm,
  DuplicateTemplateForm,
  GenerateRouteFromTemplateForm,
  BatchGenerateRoutesForm,
  BatchGenerateRoutesResult,
  GenerateRouteResult,
} from '../../types/process-route-template';

/**
 * 创建工艺路线模板 API
 * 
 * @param client 请求客户端
 * @returns API 方法集合
 */
export function createProcessRouteTemplateApi(client: IRequestClient) {
  return {
    // ========================================
    // 模板 CRUD
    // ========================================

    /**
     * 获取模板列表（分页）
     * 
     * @param params 查询参数
     * @returns 分页结果
     */
    getTemplateList: (params?: ProcessRouteTemplateQueryParams) =>
      client.get<PaginationResult<ProcessRouteTemplate>>(API_ROUTES.processRouteTemplate.list, { params }),

    /**
     * 获取所有启用的模板（不分页）
     * 
     * @description 用于下拉选择等场景
     * @returns 模板列表
     */
    getAllActiveTemplates: () =>
      client.get<ProcessRouteTemplate[]>(API_ROUTES.processRouteTemplate.active),

    /**
     * 获取模板详情
     * 
     * @param id 模板ID
     * @returns 模板详情（含工序）
     */
    getTemplateDetail: (id: number) =>
      client.get<ProcessRouteTemplate>(API_ROUTES.processRouteTemplate.detail(id)),

    /**
     * 创建模板
     * 
     * @description 支持同时创建工序
     * @param data 创建表单
     * @returns 创建的模板
     */
    createTemplate: (data: CreateProcessRouteTemplateForm) =>
      client.post<ProcessRouteTemplate>(API_ROUTES.processRouteTemplate.create, data),

    /**
     * 更新模板
     * 
     * @param id 模板ID
     * @param data 更新表单
     * @returns 更新后的模板
     */
    updateTemplate: (id: number, data: UpdateProcessRouteTemplateForm) =>
      client.put<ProcessRouteTemplate>(API_ROUTES.processRouteTemplate.processRouteTemplates(id), data),

    /**
     * 删除模板
     * 
     * @param id 模板ID
     */
    deleteTemplate: (id: number) =>
      client.delete<void>(API_ROUTES.processRouteTemplate.delete(id)),

    /**
     * 复制模板
     * 
     * @param id 模板ID
     * @param data 复制表单
     * @returns 新模板
     */
    duplicateTemplate: (id: number, data: DuplicateTemplateForm) =>
      client.post<ProcessRouteTemplate>(API_ROUTES.processRouteTemplate.duplicate(id), data),

    // ========================================
    // 模板工序管理
    // ========================================

    /**
     * 添加工序到模板
     * 
     * @param templateId 模板ID
     * @param data 工序数据
     * @returns 创建的工序
     */
    addTemplateOperation: (templateId: number, data: TemplateOperationForm) =>
      client.post<ProcessRouteTemplateOperation>(API_ROUTES.processRouteTemplate.operations(templateId), data),

    /**
     * 更新模板工序
     * 
     * @param operationId 工序ID
     * @param data 更新数据
     * @returns 更新后的工序
     */
    updateTemplateOperation: (operationId: number, data: TemplateOperationForm) =>
      client.put<ProcessRouteTemplateOperation>(
        API_ROUTES.processRouteTemplateOperation.processRouteTemplateOperations(operationId),
        data,
      ),

    /**
     * 删除模板工序
     * 
     * @param operationId 工序ID
     */
    deleteTemplateOperation: (operationId: number) =>
      client.delete<void>(API_ROUTES.processRouteTemplateOperation.delete(operationId)),

    // ========================================
    // 基于模板生成工艺路线
    // ========================================

    /**
     * 基于模板生成工艺路线
     * 
     * @description 为指定物料基于模板生成一条新的工艺路线
     * 
     * @param templateId 模板ID
     * @param data 生成参数
     * @returns 生成的工艺路线信息
     * 
     * @example
     * ```typescript
     * const result = await api.generateRouteFromTemplate(1, {
     *   material_id: 100,
     *   route_code: 'RT-001',  // 可选
     *   route_name: '产品A工艺路线',  // 可选
     *   version: 'V1.0',
     *   is_default: true
     * })
     * ```
     */
    generateRouteFromTemplate: (templateId: number, data: GenerateRouteFromTemplateForm) =>
      client.post<GenerateRouteResult>(API_ROUTES.processRouteTemplate.generate(templateId), data),

    /**
     * 批量基于模板生成工艺路线
     * 
     * @description 为多个物料批量生成工艺路线
     * 
     * @param templateId 模板ID
     * @param data 批量生成参数
     * @returns 生成结果
     * 
     * @example
     * ```typescript
     * const result = await api.batchGenerateRoutesFromTemplate(1, {
     *   materials: [
     *     { material_id: 100 },
     *     { material_id: 101, route_code: 'RT-CUSTOM' },
     *     { material_id: 102, is_default: true }
     *   ]
     * })
     * console.log(`成功: ${result.success_count}, 失败: ${result.failed_count}`)
     * ```
     */
    batchGenerateRoutesFromTemplate: (templateId: number, data: BatchGenerateRoutesForm) =>
      client.post<BatchGenerateRoutesResult>(
        API_ROUTES.processRouteTemplate.batchGenerate(templateId),
        data,
      ),
  };
}

// 导出类型
export type ProcessRouteTemplateApi = ReturnType<typeof createProcessRouteTemplateApi>;
